import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    FeedbackEvent,
    MessageFeedbackState,
    ConversationFeedbackState,
    FeedbackSentiment,
    FeedbackCategory,
    PersistedFeedbackState,
    FeedbackUIState
} from './feedback-state.interface';

/**
 * Feedback State Management Service
 * Manages feedback as first-class application state
 * Integrates with conversation state and persists across sessions
 */
@Injectable({
    providedIn: 'root'
})
export class FeedbackStateManagerService {
    // State management
    private readonly feedbackStates = new Map<string, ConversationFeedbackState>();
    private readonly feedbackStateSubject$ = new BehaviorSubject<Map<string, ConversationFeedbackState>>(
        this.feedbackStates
    );
    private readonly activeFeedbackSubject$ = new BehaviorSubject<MessageFeedbackState | null>(null);
    private readonly feedbackUIStateSubject$ = new BehaviorSubject<FeedbackUIState>({
        showPrompt: false,
        isSubmitting: false,
        showSuccess: false,
        showError: false,
        selectedCategories: [],
        selectedReasons: [],
        comment: ''
    });

    // Event stream for feedback events (aligned with DDD)
    private readonly feedbackEventSubject$ = new BehaviorSubject<FeedbackEvent | null>(null);

    // Public observables
    public readonly feedbackStates$ = this.feedbackStateSubject$.asObservable();
    public readonly activeFeedback$ = this.activeFeedbackSubject$.asObservable();
    public readonly feedbackUIState$ = this.feedbackUIStateSubject$.asObservable();
    public readonly feedbackEvents$ = this.feedbackEventSubject$.asObservable();

    constructor() {
        // console.log('🎯 FeedbackStateManagerService initialized');
    }

    /**
     * Initialize feedback for a message
     */
    initializeFeedback(conversationId: string, messageId: string, requestId: string): MessageFeedbackState {
        // console.log(`📝 Initializing feedback for message ${messageId} in conversation ${conversationId}`);

        // Get or create conversation feedback state
        let conversationState = this.feedbackStates.get(conversationId);
        if (!conversationState) {
            conversationState = this.createConversationFeedbackState(conversationId);
            this.feedbackStates.set(conversationId, conversationState);
        }

        // Create message feedback state
        const feedbackState: MessageFeedbackState = {
            feedback_id: `feedback_${messageId}_${Date.now()}`,
            message_id: messageId,
            request_id: requestId,
            status: 'pending',
            prompted_at: Date.now()
        };

        // Add to conversation state
        conversationState.feedbackByMessage.set(messageId, feedbackState);

        // Update active feedback
        this.activeFeedbackSubject$.next(feedbackState);

        // Update UI state
        this.updateUIState({
            showPrompt: true,
            isSubmitting: false,
            showSuccess: false,
            showError: false
        });

        // Emit state change
        this.feedbackStateSubject$.next(new Map(this.feedbackStates));

        return feedbackState;
    }

    /**
     * Submit feedback for a message
     */
    submitFeedback(
        feedbackState: MessageFeedbackState,
        sentiment: FeedbackSentiment,
        data: {
            categories?: FeedbackCategory[];
            reasons?: string[];
            comment?: string;
            userEmail?: string;
            userId?: string;
        }
    ): FeedbackEvent {
        // console.log(`✅ Submitting feedback ${feedbackState.feedback_id} with sentiment: ${sentiment}`);

        // Update feedback state
        feedbackState.sentiment = sentiment;
        feedbackState.categories = data.categories;
        feedbackState.reasons = data.reasons;
        feedbackState.comment = data.comment;
        feedbackState.user_email = data.userEmail;
        feedbackState.user_id = data.userId;
        feedbackState.status = 'submitted';
        feedbackState.submitted_at = Date.now();

        // Update conversation metrics
        const conversationId = this.findConversationIdForMessage(feedbackState.message_id);
        if (conversationId) {
            const conversationState = this.feedbackStates.get(conversationId);
            if (conversationState) {
                this.updateConversationMetrics(conversationState, sentiment);
                this.feedbackStateSubject$.next(new Map(this.feedbackStates));
            }
        }

        // Create feedback event (DDD aligned)
        const feedbackEvent: FeedbackEvent = {
            event_type: 'craig.feedback.submitted',
            event_id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: Date.now(),
            conversation_id: conversationId || '',
            message_id: feedbackState.message_id,
            request_id: feedbackState.request_id,
            feedback: {
                feedback_id: feedbackState.feedback_id,
                sentiment,
                categories: data.categories,
                reasons: data.reasons,
                comment: data.comment,
                user_email: data.userEmail,
                user_id: data.userId,
                submitted_at: feedbackState.submitted_at!
            },
            domain: {
                name: 'feedback',
                subdomain: 'user_interaction'
            }
        };

        // Emit event
        this.feedbackEventSubject$.next(feedbackEvent);

        // Update UI state
        this.updateUIState({
            showPrompt: false,
            isSubmitting: false,
            showSuccess: true,
            showError: false
        });

        // Persist state
        if (conversationId) {
            this.saveFeedbackState(conversationId);
        }

        return feedbackEvent;
    }

    /**
     * Acknowledge feedback (mark as processed)
     */
    acknowledgeFeedback(feedbackId: string): void {
        // console.log(`👍 Acknowledging feedback ${feedbackId}`);

        // Find and update feedback state
        for (const [_, conversationState] of this.feedbackStates) {
            for (const [_, feedbackState] of conversationState.feedbackByMessage) {
                if (feedbackState.feedback_id === feedbackId) {
                    feedbackState.status = 'acknowledged';
                    feedbackState.acknowledged_at = Date.now();
                    this.feedbackStateSubject$.next(new Map(this.feedbackStates));
                    return;
                }
            }
        }
    }

    /**
     * Save feedback state to localStorage
     */
    saveFeedbackState(conversationId: string): void {
        const conversationState = this.feedbackStates.get(conversationId);
        if (!conversationState) return;

        const persistedState: PersistedFeedbackState = {
            version: '3.0',
            conversation_id: conversationId,
            session_id: conversationState.session_id,
            feedbacks: Array.from(conversationState.feedbackByMessage.values()),
            metrics: {
                total: conversationState.totalFeedbacks,
                positive: conversationState.positiveFeedbacks,
                negative: conversationState.negativeFeedbacks,
                neutral: conversationState.neutralFeedbacks
            },
            persisted_at: Date.now()
        };

        const storageKey = `craig_feedback_state_${conversationId}`;
        localStorage.setItem(storageKey, JSON.stringify(persistedState));
        // console.log(`💾 Saved feedback state for conversation ${conversationId}`);
    }

    /**
     * Load feedback state from localStorage
     */
    loadFeedbackState(conversationId: string): ConversationFeedbackState | null {
        const storageKey = `craig_feedback_state_${conversationId}`;
        const savedState = localStorage.getItem(storageKey);

        if (!savedState) {
            // console.log(`📭 No saved feedback state for conversation ${conversationId}`);
            return null;
        }

        try {
            const persistedState: PersistedFeedbackState = JSON.parse(savedState);

            // Reconstruct conversation state
            const conversationState: ConversationFeedbackState = {
                conversation_id: persistedState.conversation_id,
                session_id: persistedState.session_id,
                feedbackByMessage: new Map(),
                totalFeedbacks: persistedState.metrics.total,
                positiveFeedbacks: persistedState.metrics.positive,
                negativeFeedbacks: persistedState.metrics.negative,
                neutralFeedbacks: persistedState.metrics.neutral,
                firstFeedbackAt: undefined,
                lastFeedbackAt: undefined
            };

            // Restore feedback states
            persistedState.feedbacks.forEach((feedback) => {
                conversationState.feedbackByMessage.set(feedback.message_id, feedback);

                // Update timestamps
                if (feedback.submitted_at) {
                    if (
                        !conversationState.firstFeedbackAt ||
                        feedback.submitted_at < conversationState.firstFeedbackAt
                    ) {
                        conversationState.firstFeedbackAt = feedback.submitted_at;
                    }
                    if (!conversationState.lastFeedbackAt || feedback.submitted_at > conversationState.lastFeedbackAt) {
                        conversationState.lastFeedbackAt = feedback.submitted_at;
                    }
                }
            });

            // Update state
            this.feedbackStates.set(conversationId, conversationState);
            this.feedbackStateSubject$.next(new Map(this.feedbackStates));

            // console.log(`📥 Loaded feedback state for conversation ${conversationId}`, conversationState);
            return conversationState;
        } catch (error) {
            console.error(`❌ Failed to load feedback state for conversation ${conversationId}`, error);
            return null;
        }
    }

    /**
     * Get feedback for a specific message
     */
    getFeedbackForMessage(messageId: string): MessageFeedbackState | null {
        for (const [_, conversationState] of this.feedbackStates) {
            const feedbackState = conversationState.feedbackByMessage.get(messageId);
            if (feedbackState) return feedbackState;
        }
        return null;
    }

    /**
     * Get feedback state for a conversation
     */
    getFeedbackForConversation(conversationId: string): ConversationFeedbackState | null {
        return this.feedbackStates.get(conversationId) || null;
    }

    /**
     * Get aggregated metrics for a conversation
     */
    getAggregatedMetrics(conversationId: string): any {
        const conversationState = this.feedbackStates.get(conversationId);
        if (!conversationState) return null;

        const feedbacks = Array.from(conversationState.feedbackByMessage.values());
        const submittedFeedbacks = feedbacks.filter((f) => f.status === 'submitted');

        // Calculate response times
        const responseTimes = submittedFeedbacks
            .filter((f) => f.submitted_at && f.prompted_at)
            .map((f) => f.submitted_at! - f.prompted_at);

        const avgResponseTime =
            responseTimes.length > 0 ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length : 0;

        // Category distribution
        const categoryDistribution = new Map<FeedbackCategory, number>();
        submittedFeedbacks.forEach((f) => {
            f.categories?.forEach((cat) => {
                categoryDistribution.set(cat, (categoryDistribution.get(cat) || 0) + 1);
            });
        });

        return {
            totalFeedbacks: conversationState.totalFeedbacks,
            sentimentDistribution: {
                positive: conversationState.positiveFeedbacks,
                negative: conversationState.negativeFeedbacks,
                neutral: conversationState.neutralFeedbacks
            },
            averageResponseTime: avgResponseTime,
            categoryDistribution: Object.fromEntries(categoryDistribution),
            submissionRate: feedbacks.length > 0 ? (submittedFeedbacks.length / feedbacks.length) * 100 : 0
        };
    }

    /**
     * Update UI state
     */
    updateUIState(updates: Partial<FeedbackUIState>): void {
        const currentState = this.feedbackUIStateSubject$.value;
        this.feedbackUIStateSubject$.next({ ...currentState, ...updates });
    }

    /**
     * Reset feedback state for a conversation
     */
    resetConversationFeedback(conversationId: string): void {
        this.feedbackStates.delete(conversationId);
        this.feedbackStateSubject$.next(new Map(this.feedbackStates));
        // console.log(`🔄 Reset feedback state for conversation ${conversationId}`);
    }

    /**
     * Clear all feedback states
     */
    clearAllFeedbackStates(): void {
        this.feedbackStates.clear();
        this.feedbackStateSubject$.next(new Map());
        this.activeFeedbackSubject$.next(null);
        // console.log('🗑️ Cleared all feedback states');
    }

    // Private helper methods

    private createConversationFeedbackState(conversationId: string): ConversationFeedbackState {
        return {
            conversation_id: conversationId,
            session_id: `session_${Date.now()}`,
            feedbackByMessage: new Map(),
            totalFeedbacks: 0,
            positiveFeedbacks: 0,
            negativeFeedbacks: 0,
            neutralFeedbacks: 0
        };
    }

    private findConversationIdForMessage(messageId: string): string | null {
        for (const [conversationId, conversationState] of this.feedbackStates) {
            if (conversationState.feedbackByMessage.has(messageId)) {
                return conversationId;
            }
        }
        return null;
    }

    private updateConversationMetrics(
        conversationState: ConversationFeedbackState,
        sentiment: FeedbackSentiment
    ): void {
        conversationState.totalFeedbacks++;

        switch (sentiment) {
            case 'positive':
                conversationState.positiveFeedbacks++;
                break;
            case 'negative':
                conversationState.negativeFeedbacks++;
                break;
            case 'neutral':
                conversationState.neutralFeedbacks++;
                break;
        }

        conversationState.lastFeedbackAt = Date.now();
        if (!conversationState.firstFeedbackAt) {
            conversationState.firstFeedbackAt = Date.now();
        }
    }
}
